import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Copy, Check, Loader2, Key, Eye, EyeOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { ProviderLayout } from './ProviderLayout';

function formatDate(str) {
  if (!str) return '—';
  try { return new Date(str).toLocaleString('de-DE', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }); }
  catch { return str; }
}

export function ProviderApiKeys() {
  const { authHeaders, API } = useAdminAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createdKey, setCreatedKey] = useState(null); // shown once
  const [copied, setCopied] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/provider/api-keys`, { headers: authHeaders() });
      setKeys(r.data);
    } finally { setLoading(false); }
  }, [API, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const createKey = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const r = await axios.post(`${API}/provider/api-keys`,
        { name: newKeyName || 'API Key' }, { headers: authHeaders() });
      setCreatedKey(r.data);
      setNewKeyName('');
      setShowCreate(false);
      await load();
    } finally { setCreating(false); }
  };

  const deleteKey = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API}/provider/api-keys/${id}`, { headers: authHeaders() });
      setKeys(k => k.filter(x => x.id !== id));
    } finally { setDeletingId(null); }
  };

  const copy = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <ProviderLayout>
    <div className="p-4 sm:p-6 md:p-8 w-full">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">API-Schlüssel</h1>
          <p className="text-sm text-slate-500 mt-1">Verwalte deine API-Schlüssel für den Zugriff</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="create-api-key-btn"
          className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors text-sm">
          <Plus className="w-4 h-4" /> Neuer Schlüssel
        </button>
      </div>

    <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5" data-testid="api-keys-section">
      <p className="text-xs text-slate-500 mb-4">
        Nutze API-Schlüssel um dein Profil programmatisch zu bearbeiten.<br />
        Endpoint: <code className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-300">PUT /api/provider/me</code> mit Header{' '}
        <code className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-300">X-API-Key: &lt;key&gt;</code>
      </p>

      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-500" /></div>
      ) : keys.length === 0 ? (
        <p className="text-sm text-slate-600 py-2 text-center">Noch keine API-Schlüssel erstellt</p>
      ) : (
        <div className="space-y-2">
          {keys.map(k => (
            <div key={k.id} data-testid={`api-key-row-${k.id}`}
              className="flex items-center justify-between px-4 py-2.5 bg-slate-800/50 rounded-lg">
              <div>
                <p className="text-sm text-white font-medium">{k.name}</p>
                <p className="text-xs text-slate-500 font-mono mt-0.5">{k.key_preview}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-600">{formatDate(k.created_at)}</span>
                <button onClick={() => setDeletingId(k.id === deletingId ? null : k.id)}
                  data-testid={`delete-key-${k.id}`}
                  className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                  {deletingId === k.id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Trash2 className="w-3.5 h-3.5" />}
                </button>
                <Dialog open={deletingId === k.id && typeof deletingId === 'number'}
                  onOpenChange={open => !open && setDeletingId(null)}>
                  <DialogContent className="bg-[#0f172a] border-slate-700 text-white max-w-sm">
                    <DialogHeader><DialogTitle>Schlüssel löschen?</DialogTitle></DialogHeader>
                    <p className="text-sm text-slate-400">
                      <span className="text-white font-medium">{k.name}</span> wird unwiderruflich gelöscht.
                    </p>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => deleteKey(k.id)} data-testid="confirm-delete-key"
                        className="flex-1 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 text-sm transition-colors">
                        Löschen
                      </button>
                      <button onClick={() => setDeletingId(null)}
                        className="flex-1 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 text-sm transition-colors">
                        Abbrechen
                      </button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#0f172a] border-slate-700 text-white max-w-sm">
          <DialogHeader><DialogTitle>Neuer API-Schlüssel</DialogTitle></DialogHeader>
          <form onSubmit={createKey} className="space-y-4" data-testid="create-key-form">
            <div>
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-1.5">Name (optional)</label>
              <input value={newKeyName} onChange={e => setNewKeyName(e.target.value)}
                data-testid="new-key-name" placeholder="z.B. Produktions-Key"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 transition-colors" />
            </div>
            <button type="submit" disabled={creating} data-testid="create-key-submit"
              className="w-full py-2.5 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
              {creating && <Loader2 className="w-4 h-4 animate-spin" />}
              Schlüssel erstellen
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Show created key once */}
      <Dialog open={!!createdKey} onOpenChange={open => !open && setCreatedKey(null)}>
        <DialogContent className="bg-[#0f172a] border-slate-700 text-white max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-green-400">
            <Check className="w-4 h-4" /> API-Schlüssel erstellt
          </DialogTitle></DialogHeader>
          <p className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
            Kopiere diesen Schlüssel jetzt – er wird nur einmal angezeigt!
          </p>
          <div className="mt-2">
            <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5">
              <code className="flex-1 text-xs text-slate-300 font-mono break-all" data-testid="created-key-value">
                {showFull ? createdKey?.key : createdKey?.key_preview}
              </code>
              <button onClick={() => setShowFull(v => !v)} className="text-slate-500 hover:text-slate-300 flex-shrink-0">
                {showFull ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button onClick={() => copy(createdKey?.key)} data-testid="copy-key-btn"
                className="flex-shrink-0 text-slate-500 hover:text-cyan-400 transition-colors">
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button onClick={() => setCreatedKey(null)} data-testid="close-key-dialog"
            className="w-full mt-2 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 text-sm transition-colors">
            Schließen
          </button>
        </DialogContent>
      </Dialog>
    </div>
    </div>
    </ProviderLayout>
  );
}
